import useSound from "use-sound";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { useContext, useEffect, useState } from "react";

import { AvatarAssetType } from "@app/api/models/user";
import { useDefaultProducts } from "@app/api/product";

import Context from "@app/contexts/Context";

import { DefaultRouteProps  } from "@app/App";

import { getLanguage } from "@app/i18n/languageStorage";

import { QUIZZ_SERVER_URL } from "@app/config";

import CharactersPlaying from "@app/components/CharactersPlaying";

import { getUsersVisible } from "@app/utils/character";

import { User } from "@app/types/user";
import { Answers, ClassicAnswer, Locales, Question, QuestionLocales, RoundType } from "@app/types/question";

import styles from "@app/assets/styles/game.module.scss";

import { GameImages } from "@app/assets/images/game/game.js";
import ContestUsersLifes from "@app/components/ContestUsersLifes";

export type EmoteType = {
  userId: string;
  emoteId: string;
};

export default function Game(props: DefaultRouteProps ) {
    const { receiverContext } = props;

    const locale = getLanguage();
    const { data: characterData } = useDefaultProducts();

    const {
        ioSocket,
        setRoomId,
        setIoSocket,
        roomType,
        roomId,
    } = useContext(Context);

    const navigate = useNavigate();
    const [question, setQuestion] = useState<Question | undefined>();
    const [players, setPlayers] = useState<User[]>([]);
    const [answers, setAnswers] = useState<Answers>();
    const [roundType, setRoundType] = useState<RoundType>();
    const [answer, setAnswer] = useState<number>();
    const [timerImage, setTimerImage] = useState(GameImages.timer.initUrl);
    const [backgroudnActiveIndex, setBackgroudnActiveIndex] = useState<0 | 1>(0); // Default background index is 0
    const [showEmote, setShowEmote] = useState<EmoteType[]>([]);
    const [showUsersLife, setShowUsersLife] = useState(false);
    const [onlyVisible, setOnlyVisible] = useState<boolean>(true);

    // Queries
    const playersQuery = useQuery(["get", "players", onlyVisible], async () => {
            const res = await fetch(`${QUIZZ_SERVER_URL}/rooms/users/room-${roomId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
            });
            let players: User[] = await res.json();
            const newUserIndex = Math.floor(Math.random() * players.length);;

            if (roomType === "tournament") {
                players = players.filter((player) => {
                if (player?.life !== undefined) {
                    return player.life > 0;
                }
                });
                setPlayers(getUsersVisible(players, players[newUserIndex].id));
            }

            if (!onlyVisible) {
                setPlayers(getUsersVisible(players, players[newUserIndex].id));
            } else {
                setPlayers(players);
            }
        },
        {
            keepPreviousData: false,
            refetchOnWindowFocus: false,
        }
    );

    // Sound
    const soundManager = {
        newRound: useSound(require("../assets/sound/game/characterPresentation.m4a")),
        timer: useSound(require("../assets/sound/timer/timer.m4a")),
        timerLow: useSound(require("../assets/sound/timer/timerLow.m4a")),
        timerEnd: useSound(require("../assets/sound/timer/timerEnd.m4a")),
        question: useSound(require("../assets/sound/game/question.m4a")),
        revealAnswer: useSound(require("../assets/sound/answer/revealAnswer.m4a")),
        buzzer: useSound(require("../assets/sound/game/buzzer.m4a")),
        geniusWinner: useSound(require("../assets/sound/genius/winner.m4a")),
        geniusEndGame: useSound(require("../assets/sound/genius/endGame.m4a")),
        applause: [
            useSound(require("../assets/sound/audience/applause1.m4a")),
            useSound(require("../assets/sound/audience/applause2.m4a")),
            useSound(require("../assets/sound/audience/applause3.m4a")),
        ],
        boo: [
            useSound(require("../assets/sound/audience/boo1.m4a")),
            useSound(require("../assets/sound/audience/boo2.m4a")),
            useSound(require("../assets/sound/audience/boo3.m4a")),
        ],
        laugh: [
            useSound(require("../assets/sound/audience/laugh1.m4a")),
            useSound(require("../assets/sound/audience/laugh2.m4a")),
            useSound(require("../assets/sound/audience/laugh3.m4a")),
        ],
        geniusGood: [
            useSound(require("../assets/sound/genius/good/good1.m4a")),
            useSound(require("../assets/sound/genius/good/good2.m4a")),
            useSound(require("../assets/sound/genius/good/good3.m4a")),
        ],
        geniusWrong: [
            useSound(require("../assets/sound/genius/wrong/wrong1.m4a")),
            useSound(require("../assets/sound/genius/wrong/wrong2.m4a")),
            useSound(require("../assets/sound/genius/wrong/wrong3.m4a")),
        ],
    }

    // Effect
    useEffect(() => {
        function exitRoom() {
            soundManager.geniusEndGame[1].stop();
            soundManager.geniusWinner[1].stop();
            soundManager.newRound[1].stop();
            soundManager.question[1].stop();
            soundManager.revealAnswer[1].stop();
            soundManager.timer[1].stop();
            soundManager.timerLow[1].stop();

            setRoomId(undefined);
            if (ioSocket) {
                ioSocket.emit("tvDisconnected");
                ioSocket.disconnect();
            }
            setIoSocket(undefined);

            if (receiverContext) {
                receiverContext.stop();
            } else {
                navigate("/");
            }
        };

        function answers(answers: Answers, currentRoundType: RoundType) {
            const { reaction } = answers;
            setTimerImage(GameImages.timer.initUrl);
            setBackgroudnActiveIndex(0);

            soundManager.question[1].stop();
            soundManager.revealAnswer[0]();
            soundManager.timer[1].stop();
            soundManager.timerLow[1].stop();

            if (reaction) {
                if (reaction.geniusReaction === "good" && reaction.geniusSoundNumber !== undefined) {
                    soundManager.geniusGood[reaction.geniusSoundNumber][0]();
                } else if (reaction.geniusReaction === "wrong" && reaction.geniusSoundNumber !== undefined) {
                    soundManager.geniusWrong[reaction.geniusSoundNumber][0]();
                }

                if (reaction.publicReaction === "applause" && reaction.publicSoundNumber !== undefined) {
                    soundManager.applause[reaction.publicSoundNumber][0]();
                } else if (reaction.publicReaction === "boo" && reaction.publicSoundNumber !== undefined) {
                    soundManager.boo[reaction.publicSoundNumber][0]();
                } else if (reaction.publicReaction === "laugh" && reaction.publicSoundNumber !== undefined) {
                    setTimeout(() => soundManager.laugh[reaction.publicSoundNumber as number][0](), 1500);
                }
            }

            setAnswers(answers);
        };

        function newQuestion(question: QuestionLocales) {
            const newQuestion = question[locale as Locales];
            setQuestion(newQuestion);

            if (newQuestion.answers.length > 0) {
                setBackgroudnActiveIndex(1);
                setTimerImage(GameImages.timer.url);
                soundManager.newRound[1].stop();
                soundManager.question[0]();
                soundManager.timer[0]();
            } else {
                setOnlyVisible(true);
                setShowUsersLife(false);
                playersQuery.refetch();
                setAnswers(undefined);
                setAnswer(undefined);
            }
        };

        function userAnswer() {
            soundManager.buzzer[0]();
        }

        function questionEnd(endOfTime: boolean) {
            if (endOfTime) {
                soundManager.timer[1].stop();
                soundManager.timerLow[1].stop();
                soundManager.timerEnd[0]();
            }
        }

        function gameEnded(winner: User, winSoundNumber: number) {
            setQuestion(undefined);
            setOnlyVisible(false);
            if (ioSocket) {
                ioSocket.off("emote", showEmote);
                ioSocket.off("answers", answers);
                ioSocket.off("timerLow", timerLow);
                ioSocket.off('newRound', newRound);
                ioSocket.off("gameEnded", gameEnded);
                ioSocket.off("deleteRoom", exitRoom);
                ioSocket.off('userAnswer', userAnswer);
                ioSocket.off('questionEnd', questionEnd);
                ioSocket.off("newQuestion", newQuestion);
                ioSocket.off("usersLifes", showUsersLifes);
            }

            if (roomType === "solo") {
                setTimeout(() => {
                    navigate("/");
                }, 1500);
            } else {
                soundManager.geniusEndGame[0]();
                setTimeout(() => {
                    soundManager.geniusWinner[0]();
                    setTimeout(() => {
                        setTimeout(() => {
                            navigate(`/game-ended/${winSoundNumber}`);
                        }, 4000);
                    }, 2500);
                }, 3500);
            }
        };

        function timerLow() {
            soundManager.timerLow[0]();
        };

        function newRound(type: RoundType) {
            setAnswers(undefined);
            soundManager.newRound[0]();
            setQuestion(undefined);
            setRoundType(type);
        }

        function showEmote(data: { emoteId: string; userId: string }) {
            const { emoteId, userId } = data;
            setShowEmote((prev) => [...prev, { emoteId, userId }]);
            setTimeout(() => {
            setShowEmote((prev) =>
                prev.filter((emote) => emote.emoteId !== emoteId && emote.userId !== userId),
            );
            }, 5000);
        }

        function showUsersLifes() {
            setOnlyVisible(false);
            setShowUsersLife(true);
        }

        if (ioSocket) {
            ioSocket.on("emote", showEmote);
            ioSocket.on("answers", answers);
            ioSocket.on("timerLow", timerLow);
            ioSocket.on('newRound', newRound);
            ioSocket.on("gameEnded", gameEnded);
            ioSocket.on("deleteRoom", exitRoom);
            ioSocket.on('userAnswer', userAnswer);
            ioSocket.on('questionEnd', questionEnd);
            ioSocket.on("newQuestion", newQuestion);
            ioSocket.on("usersLifes", showUsersLifes);
        } else {
            setRoomId(undefined);
            setIoSocket(undefined);
            navigate("/");
        }

        return () => {
            if (ioSocket) {
                ioSocket.off("emote", showEmote);
                ioSocket.off("answers", answers);
                ioSocket.off("timerLow", timerLow);
                ioSocket.off('newRound', newRound);
                ioSocket.off("gameEnded", gameEnded);
                ioSocket.off("deleteRoom", exitRoom);
                ioSocket.off('userAnswer', userAnswer);
                ioSocket.off('questionEnd', questionEnd);
                ioSocket.off("newQuestion", newQuestion);
                ioSocket.off("usersLifes", showUsersLifes);
            }
        };
    }, [soundManager]);
    
    const gridStyles: React.CSSProperties = { gridTemplateColumns: "40% 40%" };

    return (
        <div className={styles.gameContainer}>
            {showUsersLife && characterData ? (
                <ContestUsersLifes
                    players={players}
                    characterData={characterData}
                />
            ) : (
            <>
            <img src={GameImages.background[backgroudnActiveIndex]} alt=""/>
            <div className={styles.wrapper}>
                <div className={styles.charactersWrapper}>
                    <CharactersPlaying
                        players={players}
                        question={question}
                        playersAnswer={answers}
                        ioSocket={ioSocket}
                        showEmote={showEmote}
                    />
                    <img className={styles.timer} src={timerImage} alt=""/>
                </div>
                <div className={styles.questionWrapper}>
                    <img className={styles.questionInsert} src={GameImages.question.url} alt=""/>
                    <div className={styles.questionInsertText}>
                        {question?.question}
                    </div>
                    <div className={styles.grid} style={gridStyles}>
                        {question && question.answers.map((answerText, answerIndex) => {
                            let image = GameImages.answer.url;
                            const playerAnswer: User[] = [];
                            let disable = false;

                            if (answers) {
                                const classicPlayersAnswer = answers as ClassicAnswer;
                                const player = classicPlayersAnswer.usersAnswer[0];

                                if (classicPlayersAnswer.correctAnswer === answerIndex) {
                                    image = GameImages.goodAnswer.url;
                                } else if (roomType === "solo" && player.answer === answerIndex && classicPlayersAnswer.correctAnswer !== player.answer) {
                                    image = GameImages.wrongAnswer.url;
                                }

                                classicPlayersAnswer.usersAnswer.forEach(
                                    (playerAnswered) => {
                                        if (
                                        playerAnswered.userId &&
                                        playerAnswered.answer === answerIndex
                                        ) {
                                        const playerAnswerInfo = players.find(
                                            (player) => player.id === playerAnswered.userId,
                                        );

                                        if (playerAnswerInfo) {
                                            playerAnswer.push(playerAnswerInfo);
                                        }
                                        }
                                    },
                                    );
                            } else if (answer !== undefined) {
                                if (answer !== answerIndex) {
                                    disable = true; 
                                }
                            }
                            

                            return (
                                <div key={answerIndex} className={styles.buttonWrapper} style={{ height: roundType === "TrueOrFalse" ? "50%" : "100%" }}>
                                    {playerAnswer.map((user, index) => {
                                        const characterImg = characterData?.avatar
                                            ?.products.find((product) => product.id === user.characterAvatarId)
                                            ?.assets.find((asset) => asset.type === AvatarAssetType.Avatar);
                                        return (
                                            <div
                                                key={index}
                                                className={styles.characterAnswer}
                                                style={{
                                                    background: `url(${characterImg?.url}) center / contain no-repeat`,
                                                    left: `${index * 6}vh`
                                                }}
                                            />
                                        )
                                    })}
                                    <div
                                        key={answerIndex}
                                        className={styles.button}
                                        style={{
                                            background: `url(${image}) center / contain no-repeat`,
                                            opacity: disable ? 0.5 : 1,
                                        }}
                                    >
                                        {answerText}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            </>)}
        </div>
    );
}